import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import styles from './CommunityFootprintMap.module.scss'

const DEFAULT_ZOOM = 10
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%', minHeight: 200 }

// map component for both profiles, will add business specific logic later
export default function CommunityFootprintMap({
  center,
  loading,
  profileType,
  markers = [],
  className,
}) {
  const { t } = useTranslation()
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-profile',
    googleMapsApiKey: apiKey || '',
  })

  const mapCenter = useMemo(() => {
    if (center?.lat == null || center?.lng == null) return undefined
    return { lat: Number(center.lat), lng: Number(center.lng) }
  }, [center?.lat, center?.lng])

  if (!apiKey) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapNotConfigured', 'Map is not configured.')}
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapLoadError', 'Map could not be loaded.')}
        </div>
      </div>
    )
  }

  if (loading || !isLoaded) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapLoading', 'Loading map…')}
        </div>
      </div>
    )
  }

  if (!mapCenter) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.communityFootprintPlaceholder', 'Your community footprint will appear here.')}
        </div>
      </div>
    )
  }

  return (
    <div className={className || styles.mapContainer}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          fullscreenControl: true,
        }}
      >
        {/* markers for completed events can be added here in a later phase */}
        {markers.length > 0 ? null : null}
      </GoogleMap>
    </div>
  )
}

CommunityFootprintMap.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  loading: PropTypes.bool,
  profileType: PropTypes.oneOf(['user', 'business']),
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lat: PropTypes.number,
      lng: PropTypes.number,
    })
  ),
  className: PropTypes.string,
}
